import { Doc, generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  AtlassianFieldInfo,
  AtlassianIntegrationConfig,
  AtlassianIssueType,
  AtlassianProject
} from '@dadohr/shared/domainTypes/integrations/atlassian';
import { CF, FS } from '@dadohr/shared/versions';
import {
  store,
  useMappedLoadingValue
} from '../../../../../../services/firebase';
import {
  callFirebaseFunction,
  useFirebaseFunction
} from '../../../../../../services/firebaseFunctions';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../../../../services/firecache/documentListener';
import { now, ONE_MINUTE } from '../../../../../../services/time';

export const toAtlassianConfigDoc = generateToDocFn<AtlassianIntegrationConfig>();

const COLLECTION = FS.atlassianIntegrationConfig;
const collection = () => store().collection(COLLECTION);

export const atlassianConfigStore = createDocumentListenerGetter(
  (orgId) => collection().doc(orgId),
  toAtlassianConfigDoc
);

export const useAtlassianConfig = (orgId: string, userId: string) => {
  return useMappedLoadingValue(
    useDocumentListener(atlassianConfigStore(orgId)),
    (d) => {
      if (!d) {
        const n = now();
        const newConfig: Doc<AtlassianIntegrationConfig> = {
          id: orgId,
          collection: COLLECTION,
          data: {
            orgId,
            active: false,
            createdAt: n,
            createdBy: userId,
            editedAt: n,
            editedBy: userId,

            accessToken: '',
            refreshToken: '',
            expireDate: 0,
            resources: []
          }
        };
        return newConfig;
      }
      return d;
    }
  );
};

export const removeAtlassianConfig = (orgId: string) => {
  return collection().doc(orgId).delete();
};

export const getAtlassianProjects = async (
  orgId: string
): Promise<AtlassianProject[]> => {
  // TODO: FOR MIGRATION
  return callFirebaseFunction<AtlassianProject[]>(
    CF.atlassian.getProjects,
    {
      orgId
    },
    550000,
    'atlassian'
  );
};

export const useAtlassianProjects = (orgId: string) => {
  return useFirebaseFunction<AtlassianProject[]>(
    CF.atlassian.getProjects,
    { orgId },
    [orgId],
    ONE_MINUTE * 10,
    true,
    'atlassian'
  );
};

export const useAtlassianIssueTypes = (
  orgId: string,
  projectId: string,
  tplId: string
) => {
  return useFirebaseFunction<AtlassianIssueType[]>(
    CF.atlassian.getIssueTypes,
    {
      orgId,
      projectId,
      tplId
    },
    [orgId, projectId],
    ONE_MINUTE * 10,
    true,
    'atlassian'
  );
};

export const isRequiredCustomField = (field: AtlassianFieldInfo) =>
  field.required &&
  !['description', 'issuetype', 'project', 'reporter', 'summary'].includes(
    field.key
  );

export const isSupportedField = (field: AtlassianFieldInfo) => {
  if (
    field.schema.type === 'sd-customerrequesttype' ||
    field.schema.custom === 'com.atlassian.servicedesk:sd-request-participants'
  )
    return true;

  return isRequiredCustomField(field);
};
